import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { OptionSymbol } from '@/components/ui/OptionSymbol';
import { formatCurrency, formatDTE } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { consolidateLegs, type ConsolidatedLeg } from '@/lib/consolidateLegs';
import type { PositionLeg } from '@steward/shared';

interface PositionLegsTableProps {
  legs: PositionLeg[];
  showRemaining?: boolean;
  /**
   * Required, never optional-with-a-default: a table that silently defaulted
   * to "consolidated" would render fine in a host that forgot to wire the
   * toggle, hiding the fact that it's showing a merged, non-raw view.
   */
  showFills: boolean;
}

export function PositionLegsTable({ legs, showRemaining = false, showFills }: PositionLegsTableProps) {
  // Same shape either way (fillCount always present) so the row-rendering
  // below doesn't need to branch on which mode produced it.
  const rows: ConsolidatedLeg[] = showFills
    ? legs.map((leg) => ({ ...leg, fillCount: 1 }))
    : consolidateLegs(legs);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Action</TableHead>
          <TableHead>Option</TableHead>
          <TableHead className="text-center">Qty</TableHead>
          {showRemaining && <TableHead className="text-center">Remaining</TableHead>}
          <TableHead className="text-right">Price</TableHead>
          <TableHead>Expiration</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((leg) => {
          const isSell = leg.action === 'STO' || leg.action === 'STC';
          return (
            <TableRow key={leg.id}>
              <TableCell>
                <span
                  className={cn(
                    'font-mono text-sm font-medium',
                    isSell ? 'text-green-600' : 'text-red-600'
                  )}
                >
                  {leg.action}
                </span>
              </TableCell>
              <TableCell>
                <OptionSymbol symbol={leg.optionSymbol} />
              </TableCell>
              <TableCell className="text-center">
                {leg.quantity}
                {leg.fillCount > 1 && (
                  <span className="text-gray-400 text-xs ml-1">({leg.fillCount} fills)</span>
                )}
              </TableCell>
              {showRemaining && (
                <TableCell className="text-center">
                  <span
                    className={cn(
                      leg.quantityRemaining === 0 && 'text-gray-400',
                      leg.quantityRemaining > 0 && leg.quantityRemaining < leg.quantity && 'text-yellow-600'
                    )}
                  >
                    {leg.quantityRemaining}
                  </span>
                </TableCell>
              )}
              <TableCell className="text-right font-mono">
                {formatCurrency(leg.pricePerContract)}
              </TableCell>
              <TableCell>
                <span className="text-sm text-gray-500">{formatDTE(leg.expirationDate)}</span>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
